import { Link } from 'react-router-dom'
import { motion, useScroll, useTransform } from 'framer-motion'
import { BarChart3, Megaphone, GitBranch } from 'lucide-react'
import { useRef } from 'react'

const approach = [
  { icon: BarChart3, title: 'Strategic brand positioning', href: '/services/social-media-marketing' },
  { icon: Megaphone, title: 'Meta Ads driven lead generation', href: '/services/paid-ads' },
  { icon: GitBranch, title: 'Conversion-focused sales systems', href: '/services/funnel-building' },
]

export function WhoWeAre() {
  const containerRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start']
  })
  
  const y = useTransform(scrollYProgress, [0, 1], [50, -50])
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0])

  return (
    <section ref={containerRef} className="section-pad px-4 sm:px-6 lg:px-8 bg-slate-50/80 dark:bg-slate-900/40 relative overflow-hidden">
      <motion.div 
        style={{ y, opacity }}
        className="absolute -right-20 top-20 w-64 h-64 lg:w-96 lg:h-96 pointer-events-none hidden md:flex items-center justify-center"
      >
        <div className="absolute w-full h-full bg-gradient-to-tr from-primary/20 to-accent/20 rounded-[40%_60%_70%_30%/40%_50%_60%_50%] animate-float blur-2xl" />
        <div className="absolute w-3/4 h-3/4 border border-primary/20 dark:border-accent/20 rounded-full animate-[spin_10s_linear_infinite]" />
        <div className="absolute w-1/2 h-1/2 border border-accent/30 dark:border-primary/30 rounded-full animate-[spin_15s_linear_infinite_reverse]" />
      </motion.div>

      <div className="container-narrow relative z-10">
        <motion.h2
          className="section-heading text-center mb-3"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          Who We Are
        </motion.h2>
        <motion.p
          className="section-sub text-center text-accent font-semibold mb-8 sm:mb-10"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.05, duration: 0.5 }}
        >
          Strategy. Systems. Sustainable Growth.
        </motion.p>
        <motion.div
          className="space-y-5 sm:space-y-6 text-slate-600 dark:text-slate-400 text-center text-sm sm:text-base max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1, duration: 0.5 }}
        >
          <p>
            DigiSutra is a growth-focused digital agency built for businesses that want structured and predictable marketing outcomes.
          </p>
          <p>
            We work with brands where every lead matters and every opportunity must be handled with care.
          </p>
          <p className="font-medium text-slate-700 dark:text-slate-300">Our approach combines:</p>
        </motion.div>
        <motion.div
          className="mt-8 sm:mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.15, duration: 0.5 }}
        >
          {approach.map(({ icon: Icon, title, href }) => (
            <Link key={title} to={href}>
              <motion.div
                className="group card-glass flex flex-col items-center p-6 sm:p-8 text-center rounded-2xl"
                whileHover={{ y: -6, scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <motion.div
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-primary/10 dark:bg-accent/10 flex items-center justify-center"
                  whileHover={{ rotate: [0, -5, 5, 0], transition: { duration: 0.4 } }}
                >
                  <Icon className="text-primary dark:text-accent" size={28} strokeWidth={1.8} />
                </motion.div>
                <p className="mt-4 font-medium text-slate-800 dark:text-slate-200 text-sm sm:text-base group-hover:text-primary dark:group-hover:text-accent transition-colors">{title}</p>
              </motion.div>
            </Link>
          ))}
        </motion.div>
        <motion.p
          className="mt-8 sm:mt-10 text-center text-slate-600 dark:text-slate-400 italic text-sm sm:text-base max-w-2xl mx-auto"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          We believe marketing should not feel complicated. When the right systems are built, growth becomes repeatable and sustainable.
        </motion.p>
      </div>
    </section>
  )
}
